import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook pour gérer l'expiration de session après une période d'inactivité
 * @param {number} timeoutMinutes - Durée d'inactivité avant expiration (en minutes)
 * @param {Function} onExpire - Callback appelé quand la session expire
 */
export const useSessionTimeout = (timeoutMinutes = 30, onExpire) => {
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Fonction pour réinitialiser le timer
  const resetTimer = useCallback(() => {
    // Nettoyer le timer existant
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Mettre à jour le timestamp de dernière activité
    lastActivityRef.current = Date.now();

    // Créer un nouveau timer
    const timeoutMs = timeoutMinutes * 60 * 1000; // Convertir minutes en millisecondes
    timeoutRef.current = setTimeout(() => {
      console.log('⏰ Session expirée après', timeoutMinutes, 'minutes d\'inactivité');
      if (onExpire) {
        onExpire();
      }
    }, timeoutMs);
  }, [timeoutMinutes, onExpire]);

  // Fonction pour détecter l'activité utilisateur
  const handleActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    
    // Seulement réinitialiser si au moins 1 seconde s'est écoulée (éviter trop de réinitialisations)
    if (timeSinceLastActivity > 1000) {
      resetTimer();
    }
  }, [resetTimer]);

  useEffect(() => {
    // Événements à écouter pour détecter l'activité
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Ajouter les listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Initialiser le timer
    resetTimer();

    // Nettoyer les listeners et le timer au démontage
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleActivity, resetTimer]);

  // Fonction pour réinitialiser manuellement le timer (utile après une action importante)
  const refreshSession = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  return { refreshSession };
};

